import React, { useState, useEffect, useCallback } from 'react';
import { imageApi, getAuthToken } from '../../utils/api';
import { X } from 'lucide-react';
import { resolveApiUrl } from '../../utils/apiBase';
import AuthenticatedImage from '../AuthenticatedImage';

interface ImageEntry {
    name: string;
    url: string;
    size: number;
    lastModified: string | null;
}

/**
 * The shared image store that article bodies and release notes both render.
 *
 * Lifted out of the admin page unchanged when it was split into tabs: it has
 * nothing to do with who has access, and only shared a page with the user list
 * because both needed an administrator.
 */
const ImageGalleryTab: React.FC = () => {
    const [images, setImages] = useState<ImageEntry[]>([]);
    const [imagesLoading, setImagesLoading] = useState(true);
    const [imagesError, setImagesError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<ImageEntry | null>(null);

    const fetchImages = useCallback(async () => {
        setImagesLoading(true);
        setImagesError(null);
        const result = await imageApi.list();
        if (result.error) {
            setImagesError(result.error);
            setImages([]);
        } else {
            setImages(result.data);
        }
        setImagesLoading(false);
    }, []);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    const handleDeleteImage = async (blobName: string) => {
        if (!window.confirm(`Delete image "${blobName}"?`)) return;
        const result = await imageApi.deleteImage(blobName);
        if (result.error) {
            setImagesError(result.error);
        } else {
            await fetchImages();
        }
    };

    /**
     * Open a stored image in a new tab.
     *
     * This was an <a href> straight at the API, which a new-tab navigation
     * fetches without a bearer token - so App Service Authentication answered
     * it with its own "You do not have permission to view this directory or
     * page." rather than the picture. The bytes are fetched here instead and
     * handed over as an object URL.
     */
    const handleViewImage = async (url: string) => {
        // Opened before the await so the click is still the user gesture a
        // popup blocker looks for; the tab is filled in once the bytes land.
        const tab = window.open('', '_blank', 'noopener,noreferrer');
        try {
            const token = await getAuthToken();
            const response = await fetch(resolveApiUrl(url), {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error(`${response.status}`);
            const objectUrl = URL.createObjectURL(await response.blob());
            if (tab) {
                // Not revoked: the new tab is the only holder, and releasing it
                // would blank the page the user just asked for.
                tab.location.href = objectUrl;
            }
        } catch {
            tab?.close();
            setImagesError('Could not open that image.');
        }
    };

    const formatSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="fade-in">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-800">Image Gallery</h3>
                    <button
                        onClick={fetchImages}
                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                        disabled={imagesLoading}
                    >
                        {imagesLoading ? 'Loading...' : `Refresh (${images.length} images)`}
                    </button>
                </div>
                {imagesError && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {imagesError}
                    </div>
                )}
                {imagesLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="spinner"></div>
                    </div>
                ) : images.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                        No images uploaded yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Preview
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Size
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Last Modified
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {images.map((img, idx) => (
                                    <tr key={img.name || `img-${idx}`} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => setSelectedImage(img)}
                                                className="w-12 h-12 rounded-lg border border-slate-200 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all"
                                                title="Click to preview"
                                            >
                                                <AuthenticatedImage
                                                    src={img.url}
                                                    alt={img.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-sm font-medium text-slate-800 truncate max-w-xs" title={img.name}>
                                                {img.name}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {formatSize(img.size)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {img.lastModified ? new Date(img.lastModified).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleViewImage(img.url)}
                                                className="text-sm text-blue-500 hover:text-blue-700 hover:underline mr-4"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => handleDeleteImage(img.name)}
                                                className="text-sm text-red-500 hover:text-red-700 hover:underline"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Image Preview Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto"
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl p-4 fade-in max-w-[90vw] max-h-[90vh] w-full flex flex-col"
                        style={{ maxHeight: '90vh', maxWidth: '90vw' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-slate-800 truncate pr-4">{selectedImage.name}</h3>
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0">
                            <AuthenticatedImage
                                src={selectedImage.url}
                                alt={selectedImage.name}
                                className="max-w-full max-h-[calc(90vh-8rem)] w-auto h-auto object-contain rounded-lg border border-slate-200"
                                style={{ maxHeight: 'calc(90vh - 8rem)' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageGalleryTab;
